import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { LuFileText } from "react-icons/lu";
import { PanelDragContext } from "../ResizeablePanel";

export interface Word {
    start: number;
    end: number;
    text: string;
}

export interface TranscriptProps {
    words: Word[];
    currentTime: number;
    onWordClick: (time: number) => void;
}

const parseVttTimestamp = (timestamp: string): number => {
    const [hours, minutes, seconds] = timestamp.split(":");
    const [secs, ms] = seconds.split(".");
    return parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseFloat(`${secs}.${ms}`);
};

export const fetchSubtitles = async (subtitleUrl: string): Promise<Word[]> => {
    if (!subtitleUrl) return [];

    try {
        const response = await axios.get(subtitleUrl);
        const vttText = response.data.trim();
        const vttLines = vttText.split("\n\n").slice(1);

        const parsedWords = vttLines
            .map((vttLine: string): Word | null => {
                const [timeLine, text] = vttLine.split("\n").slice(1);
                if (!timeLine || !text) return null;

                const [startTimestamp, endTimestamp] = timeLine.split(" --> ");
                return {
                    start: parseVttTimestamp(startTimestamp),
                    end: parseVttTimestamp(endTimestamp),
                    text: text.trim(),
                };
            })
            .filter((word: Word | null): word is Word => word !== null);

        return parsedWords;
    } catch (error) {
        console.error("Error fetching or parsing subtitles", error);
        return [];
    }
};

const groupWordsBySilentGapsAndWordCount = (
    words: Word[],
    gapThreshold: number,
    maxWords: number
): Word[][] => {
    const paragraphs: Word[][] = [];
    let currentParagraph: Word[] = [];
    let wordCount = 0;

    words.forEach((word, index) => {
        currentParagraph.push(word);
        wordCount++;

        const isEndOfSentence = /[.!?]$/.test(word.text.trim());
        const isLastWord = index === words.length - 1;
        const nextWord = words[index + 1];
        const gap = nextWord ? nextWord.start - word.end : 0;

        if (gap > gapThreshold || (wordCount >= maxWords && isEndOfSentence) || isLastWord) {
            paragraphs.push(currentParagraph);
            currentParagraph = [];
            wordCount = 0;
        }
    });

    return paragraphs;
};

const Transcript: React.FC<TranscriptProps> = ({ words, currentTime, onWordClick }) => {
    const transcriptRef = useRef<HTMLDivElement>(null);
    const currentWordRef = useRef<HTMLSpanElement>(null);
    const [showJumpButton, setShowJumpButton] = useState(false);
    const isUserScrolling = useRef(false);
    const lastInteractionTime = useRef<number>(0);
    const isPanelDragging = useContext(PanelDragContext);

    const getCurrentWord = useCallback(() => {
        return words.find(word => currentTime >= word.start && currentTime <= word.end);
    }, [words, currentTime]);

    const isCurrentWordVisible = useCallback(() => {
        if (!transcriptRef.current || !currentWordRef.current) return true;

        const containerRect = transcriptRef.current.getBoundingClientRect();
        const wordRect = currentWordRef.current.getBoundingClientRect();
        const buffer = 50;

        return (
            wordRect.top >= containerRect.top - buffer &&
            wordRect.bottom <= containerRect.bottom + buffer
        );
    }, []);

    const scrollToCurrentWord = useCallback((immediate = false) => {
        if (!transcriptRef.current || !currentWordRef.current) return;

        const containerRect = transcriptRef.current.getBoundingClientRect();
        const wordRect = currentWordRef.current.getBoundingClientRect();
        const containerHeight = transcriptRef.current.clientHeight;

        const idealScrollTop =
            transcriptRef.current.scrollTop +
            (wordRect.top - containerRect.top) -
            containerHeight / 2 +
            wordRect.height / 2;

        transcriptRef.current.scrollTo({
            top: idealScrollTop,
            behavior: immediate ? "auto" : "smooth",
        });
    }, []);

    const handleManualScroll = useCallback(() => {
        const now = Date.now();
        if (now - lastInteractionTime.current < 50) {
            isUserScrolling.current = true;
            setShowJumpButton(!isCurrentWordVisible());
        }
    }, [isCurrentWordVisible]);

    const handleUserInteraction = useCallback(() => {
        lastInteractionTime.current = Date.now();
    }, []);

    const handleJumpToCurrent = useCallback(() => {
        isUserScrolling.current = false;
        setShowJumpButton(false);
        scrollToCurrentWord(true);
    }, [scrollToCurrentWord]);

    useEffect(() => {
        if (!isUserScrolling.current && getCurrentWord()) {
            scrollToCurrentWord(false);
        }
    }, [currentTime, getCurrentWord, scrollToCurrentWord]);

    const paragraphs = groupWordsBySilentGapsAndWordCount(words, 1.0, 70);

    return (
        <div className="flex h-full flex-col relative">
            <div className="border-b border-gray-200 px-4 py-3">
                <h3 className="text-xl font-semibold">Transcription</h3>
            </div>
            <div
                ref={transcriptRef}
                className="flex-grow overflow-y-auto overflow-x-hidden scroll-smooth relative p-4"
                onScroll={handleManualScroll}
                onWheel={handleUserInteraction}
                onTouchStart={handleUserInteraction}
                onMouseDown={handleUserInteraction}
            >
                {paragraphs.map((paragraph, pIndex) => (
                    <p key={pIndex} className="leading-relaxed mb-8 break-words">
                        {paragraph.map((word, index) => (
                            <span
                                key={`${pIndex}-${index}`}
                                ref={
                                    currentTime >= word.start && currentTime <= word.end
                                        ? currentWordRef
                                        : null
                                }
                                className={`inline-block cursor-pointer transition-all duration-200 px-0.5 py-0.5 rounded hover:bg-violet-300 ${
                                    currentTime >= word.start && currentTime <= word.end
                                        ? "bg-primary text-white font-medium shadow-sm"
                                        : ""
                                }`}
                                onClick={() => onWordClick(word.start)}
                            >
                                {word.text}
                            </span>
                        ))}
                    </p>
                ))}
                {showJumpButton && (
                    <div className="sticky bottom-4 w-full flex justify-center">
                        <button
                            className="bg-primary hover:bg-primary-focus text-white font-medium py-2 px-4 rounded-full shadow-lg flex items-center gap-2 transition-all duration-200"
                            onClick={handleJumpToCurrent}
                        >
                            <span>Jump to current word</span>
                        </button>
                    </div>
                )}
            </div>
            <div
                className={`pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-base-100 transition-opacity duration-150 ${
                    isPanelDragging ? "opacity-100" : "opacity-0"
                }`}
            >
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
                    <LuFileText className="h-6 w-6 text-primary" />
                </div>
                <span className="text-sm font-medium text-gray-500">Transcript</span>
            </div>
        </div>
    );
};

export default Transcript;
